import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { PlanPreventionService } from '../../services/plan-prevention.service';
import { EntrepriseService } from "../../../entreprises/services/entreprise.service";
import { IntervenantService } from "../../../humain/intervenant/services/intervenant.service";

import { EntrepriseTLR } from '../../../entreprises/entities/entreprise-tlr.entity';
import { SignatairePdpEntity } from '../../entities/signataire-pdp.entity';
import { TravauxPdpEntity } from '../../entities/travaux-pdp.entity';
import { applicationConstants } from "../../../../app.constants";
import { RepresentantEntity } from '../../entities/representant.entity';
import { PlanPreventionEntity } from "../../entities/plan-prevention.entity";
import { ValideurSignataireEntity } from '../../entities/valideur-signataire.entity';

type MapValideurs = {
	[id: number]: ValideurSignataireEntity[];
}
type MapRepresentants = {
	[id: number]: RepresentantEntity[];
}
type MapIntervenant = {
	[id: number]: SignatairePdpEntity[];
}
@Component({
	selector: 'pdp-resume',
	templateUrl: 'pdp-resume.html',
	styleUrls: ['pdp-resume.css'],
})
export class PdpResumeComponent {
	// Gestion globale de la fenetre
	displaySubject = new Subject<boolean>();
	
	// Valeurs "statiques" a bind sur la vue
	entreprises!: EntrepriseTLR[];
	valideurs: MapValideurs = {};
	representants: MapRepresentants = {};
	intervenants: MapIntervenant = {};
	travaux: TravauxPdpEntity[] = [];
	numeroPdp: string = '';
	parcCodes: string = '';
	descriptionParc: string = '';
	typePdp: string = '';
	versionTechnique!: number;
	
	constructor( //
		@Inject(EntrepriseService) private entrepriseService: EntrepriseService,
		@Inject(IntervenantService) private intervenantService: IntervenantService,
		@Inject(PlanPreventionService) planPreventionService: PlanPreventionService
	) {
		// lors de la reception d'une demande d'affichage du resumé d'in pdp
		this.detailCommandsSubject.pipe(
			// charger les signataires actuels, les travaux et les entreprises
			mergeMap(
				(pdp) => {
					let travaux$ = planPreventionService.getTravaux(pdp.id);
					let valideursEtRepresentants$ = planPreventionService.getValideursEtRepresentants(pdp.id);
					let intervenants$ = planPreventionService.getIntervenants(pdp.id);
					let referentParc$ = this.intervenantService.getIntervenantById(pdp.referentParcId);
					return combineLatest([of(pdp.id), valideursEtRepresentants$, travaux$, intervenants$, referentParc$]);
				}
			),
			mergeMap(
				(data) => {
					let idEntrepriseUtilisatrice = data[4].idEntreprise ;
					let entrepriseUtilisatrice$ = this.entrepriseService.getActiveEntrepriseTLRById(data[4].idEntreprise);
					return combineLatest([of(data[0]), of(data[1]), of(data[2]), of(data[3]), entrepriseUtilisatrice$]);
				}
			)
		).subscribe(
			// puis afficher la fenetre
			(data) => {
				let valideursDuPdp = data[1].valideurSignataires.sort(($1, $2) => $1.nom.localeCompare($2.nom));
				let representantsDuPdp = data[1].representants.sort(($1, $2) => $1.intervenantNom.localeCompare($2.intervenantNom));
				let intervenantsduPdp = data[3].signataires.sort(($1, $2) => $1.nom.localeCompare($2.nom));
				this.typePdp = data[3].typePdp.toString();
				this.versionTechnique = data[3].versionTechnique;
				let entrepriseUtilisatrice = data[4];
				this.entreprises = data[2].entreprisesExterieures.map(
					(ee) => {
						let entTlr = new EntrepriseTLR();
						entTlr.id = ee.idEntrepriseExt;
						entTlr.raisonSociale = ee.raisonSociale;
						return entTlr;
					}
				);
				if (data[4]) {
					this.entreprises.push(data[4]);
				}
				this.travaux = data[2].travaux;
				this.numeroPdp = data[2].numeroPdp;
				this.parcCodes = data[2].parcCodes;
				this.descriptionParc = data[2].parcDescription;
				
				valideursDuPdp.forEach( valideur => {
					if (!this.valideurs[valideur.intervenantEntrepriseId]) {
						this.valideurs[valideur.intervenantEntrepriseId] = [];
					}
					this.valideurs[valideur.intervenantEntrepriseId].push(valideur);
				} );
				
				representantsDuPdp.forEach( representant => {
					if (!this.representants[representant.intervenantEntrepriseId]) {
						this.representants[representant.intervenantEntrepriseId] = [];
					}
					this.representants[representant.intervenantEntrepriseId].push(representant);
				} );
				
				intervenantsduPdp.forEach( intervenant => {
					if (!this.intervenants[intervenant.idEntreprise]) {
						this.intervenants[intervenant.idEntreprise] = [];
					}
//					if (this.valideurSignatairesApplicable() && this.signataires[intervenant.idEntreprise].some( $ => $.hasSigned)) {
//						// si on est sur un pdp general v3++ et qu'il existe au moins un signataire de cette entreprise qui a signé
//						intervenant.hasSigned = true;
//					}
					this.intervenants[intervenant.idEntreprise].push(intervenant);
				} );
				
				this.displaySubject.next(true);
			}
 		);
	}

	@Input() id = 'pdpResumeDialog';
	
	// Liaison de l'observable servant a commander l'affichage du composant
	private detailCommandsSubject = new SubjectRelay<PlanPreventionEntity>();
	@Input() set detailCommands(obs: Observable<PlanPreventionEntity> | undefined) {
		this.detailCommandsSubject.registerSource(obs);
	}

	// Evenements de validation de l'operation en cours (s'il y en a)
	@Output() decisionIssued = new EventEmitter<boolean>();

	onCloseRequested() {
		this.entreprises = [];
		this.valideurs = {};
		this.representants = {};
		this.intervenants = {};
		this.travaux = [];
		this.numeroPdp = '';
		this.parcCodes = '';
		this.descriptionParc = '';
		this.displaySubject.next(false);
		this.decisionIssued.emit(false);
	}
	
	valideurSignatairesApplicable() : boolean {
		return this.versionTechnique >= applicationConstants.versionInWhichValideursCanSignPdps && this.typePdp === 'GENERAL';
	}
}